/**
 * Panic - Visual log overlay for the browser
 *
 * Displays log messages as toast notifications in a DOM container.
 * Supports log levels with auto-dismiss and console mirroring.
 *
 * @module lib/panic
 */

/** Log level constants */
const LEVEL_ALL = 'all'
const LEVEL_TRACE = 'trace'
const LEVEL_DEBUG = 'debug'
const LEVEL_INFO = 'info'
const LEVEL_NOTICE = 'notice'
const LEVEL_WARNING = 'warning'
const LEVEL_ERROR = 'error'
const LEVEL_FATAL = 'fatal'

/** Lifetime in ticks (1 tick = 250ms, default 5 = 1.25s visible) */
const LIFETIME = 5

/** Tick counter for expiration */
let pit = 0

/** Ticker interval handle */
let handler: ReturnType<typeof setInterval> | undefined

/**
 * Create or retrieve the panic container element.
 *
 * @returns HTMLElement - The panic container
 */
function getOutput(): HTMLElement {
  let el = document.querySelector('panic-container') as HTMLElement | null
  if (!el) {
    el = document.createElement('panic-container')
    document.body.appendChild(el)
  }

  // Completed
  return el
}

/** Container element (lazy initialized) */
let output: HTMLElement | null = null

/**
 * Get the output container, initializing if needed.
 *
 * @returns HTMLElement - The panic container
 */
function ensureOutput(): HTMLElement {
  if (!output) {
    output = getOutput()
    // Pause ticker on hover
    output.addEventListener('mouseenter', tickerStop)
    output.addEventListener('mouseleave', tickerStart)
  }

  // Completed
  return output
}

/**
 * Start the ticker for auto-dismissing log entries.
 */
function tickerStart(): void {
  if (handler === undefined) {
    handler = setInterval(tick, 250)
  }
  // Completed
}

/**
 * Stop the ticker.
 */
function tickerStop(): void {
  if (handler !== undefined) {
    clearInterval(handler)
    handler = undefined
  }
  // Completed
}

/**
 * Tick handler - removes expired log entries.
 */
function tick(): void {
  const container = ensureOutput()
  pit++

  let el = container.firstChild as HTMLElement | null
  while (el) {
    const next = el.nextSibling as HTMLElement | null
    if (Number(el.dataset.pit) < pit) {
      container.removeChild(el)
    }
    el = next
  }

  // Stop ticker when empty
  if (container.children.length < 1 && handler !== undefined) {
    tickerStop()
  }

  // Completed
}

/**
 * Format a value into a log-displayable object.
 *
 * @param value unknown - Value to format
 * @returns { message: string, context?: string, body?: string, lifetime?: number }
 */
function format(value: unknown): { message: string; context?: string; body?: string; lifetime?: number } {
  if (typeof value === 'object' && value !== null) {
    if ('message' in value) {
      // Completed
      return value as { message: string }
    }
    if (value instanceof Error) {
      // Completed
      return { message: value.message || 'Silent error', body: `Type: ${value.name || '{undefined}'}` }
    }
  }

  if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
    // Completed
    return { message: String(value) }
  }

  // Completed
  return { message: String(value) }
}

/**
 * Create a log entry in the DOM.
 *
 * @param level string - Log level class name
 * @param data object - Formatted log data
 * @returns HTMLElement - The created log element
 */
function log(level: string, data: { message: string; context?: string; lifetime?: number }): HTMLElement {
  const container = ensureOutput()
  const el = document.createElement('panic-log')
  el.className = `level-${level}`
  el.dataset.pit = String(pit + (data.lifetime || LIFETIME) * 4)

  // Badge
  const badge = document.createElement('span')
  badge.className = 'badge'
  badge.textContent = data.context || ''
  el.appendChild(badge)

  // Message
  const msg = document.createElement('span')
  msg.className = 'message'
  msg.textContent = data.message
  el.appendChild(msg)

  container.appendChild(el)

  // Ensure ticker is running
  if (handler === undefined) {
    tickerStart()
  }

  // Completed
  return el
}

// Level-filtered log functions (replaced by setLevel)
let _trace = (..._args: unknown[]): void => {}
let _debug = (..._args: unknown[]): void => {}
let _info = (..._args: unknown[]): void => {}
let _notice = (..._args: unknown[]): void => {}
let _warning = (..._args: unknown[]): void => {}
let _error = (..._args: unknown[]): void => {}

/**
 * Log at trace level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logTrace(value: unknown, ...rest: unknown[]): void {
  console.debug('[panic:trace]', value, ...rest)
  console.trace()
  log(LEVEL_TRACE, format(value))
}

/**
 * Log at debug level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logDebug(value: unknown, ...rest: unknown[]): void {
  console.debug('[panic:debug]', value, ...rest)
  log(LEVEL_DEBUG, format(value))
}

/**
 * Log at info level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logInfo(value: unknown, ...rest: unknown[]): void {
  console.log('[panic:info]', value, ...rest)
  log(LEVEL_INFO, format(value))
}

/**
 * Log at notice level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logNotice(value: unknown, ...rest: unknown[]): void {
  console.log('[panic:notice]', value, ...rest)
  log(LEVEL_NOTICE, format(value))
}

/**
 * Log at warning level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logWarning(value: unknown, ...rest: unknown[]): void {
  console.warn('[panic:warning]', value, ...rest)
  log(LEVEL_WARNING, format(value))
}

/**
 * Log at error level.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 */
function logError(value: unknown, ...rest: unknown[]): void {
  console.error('[panic:error]', value, ...rest)
  log(LEVEL_ERROR, format(value))
}

/**
 * Log at fatal level. Throws after display.
 *
 * @param value unknown - Value to log
 * @param rest unknown[] - Additional console args
 * @throws Error - Always throws after logging
 */
function logFatal(value: unknown, ...rest: unknown[]): never {
  console.error('[panic:fatal]', value, ...rest)
  log(LEVEL_FATAL, format(value))
  throw new Error('System halted.')
}

/**
 * Set the minimum log level.
 * Levels below the threshold are silenced.
 *
 * @param level string - Minimum level to display
 */
function setLevel(level: string): void {
  // Ordered levels from lowest to highest
  const levels = [LEVEL_TRACE, LEVEL_DEBUG, LEVEL_INFO, LEVEL_NOTICE, LEVEL_WARNING, LEVEL_ERROR, LEVEL_FATAL]
  const handlers = [logTrace, logDebug, logInfo, logNotice, logWarning, logError]
  const setters = [
    (fn: typeof logTrace) => { _trace = fn },
    (fn: typeof logDebug) => { _debug = fn },
    (fn: typeof logInfo) => { _info = fn },
    (fn: typeof logNotice) => { _notice = fn },
    (fn: typeof logWarning) => { _warning = fn },
    (fn: typeof logError) => { _error = fn },
  ]

  // Resolve threshold index (LEVEL_ALL maps to LEVEL_TRACE)
  const threshold = level === LEVEL_ALL ? 0 : levels.indexOf(level)
  if (threshold === -1) {
    throw new Error('[panic] Unknown level')
  }

  // Enable levels at or above threshold, disable below
  for (let i = 0; i < setters.length; i++) {
    const setter = setters[i]!
    const handler = handlers[i]!
    setter(i >= threshold ? handler : () => {})
  }

  _info({ message: `Level set to ${level}`, context: 'PANIC' } as unknown as string)

  // Completed
}

// Default level
setLevel(LEVEL_NOTICE)

/**
 * Panic module - visual log overlay.
 * Initialize with init() after DOM is ready (no-op, kept for backward compat).
 */
// Completed
export default Object.freeze({
  LEVEL_ALL,
  LEVEL_TRACE,
  LEVEL_DEBUG,
  LEVEL_INFO,
  LEVEL_NOTICE,
  LEVEL_WARNING,
  LEVEL_ERROR,
  LEVEL_FATAL,
  trace: (...args: unknown[]): void => _trace(...args),
  debug: (...args: unknown[]): void => _debug(...args),
  info: (...args: unknown[]): void => _info(...args),
  notice: (...args: unknown[]): void => _notice(...args),
  warning: (...args: unknown[]): void => _warning(...args),
  error: (...args: unknown[]): void => _error(...args),
  fatal: logFatal,
  ticker: Object.freeze({ start: tickerStart, stop: tickerStop }),
  setLevel,
  init: async (): Promise<void> => {},
})
